import type { $Fetch } from "nitropack";

// Types mirroring the Rust entities in api/crates/core/entities/src/jarod.rs
export interface JarodAnalysisItem {
    id?: string;
    section: 'marche' | 'marche_moins' | 'a_ajuster' | 'apprentissage_marche';
    finding: string;
    quote?: string;
    concept: string;
    confidence?: 'faible' | 'moyen' | 'eleve';
}

export interface JarodInteractionSummary {
    interaction_id: string;
    expert_ref?: string;
    target_company_ref?: string;
    occurred_at?: string;
    outcome?: string;
    latest_engine_version?: string;
    open_signalement_count: number;
}

export interface JarodVersionSummary {
    extraction_id: string;
    engine_version: string;
    created_at: string;
    grounded_count: number;
    quarantined_count: number;
}

export interface JarodSignalement {
    signalement_id: string;
    item_id: string;
    reason: string;
    reported_by?: string;
    created_at: string;
    consumed: boolean;
}

export interface JarodReviewProvenance {
    interaction_id: string;
    expert_ref?: string;
    target_company_ref?: string;
    occurred_at?: string;
    outcome?: string;
    outcome_note?: string;
    transcript_purged_at?: string | null;
    latest_engine_version?: string;
}

export interface JarodReview {
    provenance: JarodReviewProvenance;
    versions: JarodVersionSummary[];
    items: JarodAnalysisItem[];
    quarantined: JarodAnalysisItem[];
    signalements: JarodSignalement[];
    current_extraction_id: string;
}

export interface JarodVersionItems {
    items: JarodAnalysisItem[];
    quarantined: JarodAnalysisItem[];
    signalements: JarodSignalement[];
}

export type JarodSkipReason = 'other_user' | 'not_connected' | 'too_short' | 'no_list' | 'unmapped_list' | 'ignored_list' | 'erased' | 'transcript_pending';

export interface JarodSyncReport {
    imported: number;
    already_imported: number;
    skipped: Partial<Record<JarodSkipReason, number>>;
    analyses_done: number;
    analyses_failed: number;
}

export interface JarodDialerConnection {
    provider: 'minari';
    api_key_last4: string;
    external_user_id: string | null;
    last_sync_at: string | null;
    last_sync_error: 'invalid_credentials' | 'rate_limited' | 'unavailable' | 'unexpected_payload' | null;
    last_sync_report: JarodSyncReport | null;
}

export interface JarodDialerUser { external_user_id: string; name: string; email: string | null; }
export interface JarodDialerList { external_list_id: string; name: string; mission_id: string | null; }
export interface JarodMissionOption { mission_id: string; name: string; company_name: string | null; }
export interface JarodDialerLists { lists: JarodDialerList[]; missions: JarodMissionOption[]; }

export type JarodAnalysisState = 'analysed' | 'pending' | 'failed';

export interface JarodImportedCall {
    interaction_id: string;
    occurred_at: string;
    mission_name: string | null;
    target_company_ref: string | null;
    duration_seconds: number | null;
    meeting_booked: boolean | null;
    analysis_state: JarodAnalysisState;
}

export interface JarodCallsPage { calls: JarodImportedCall[]; next_cursor: string | null; total: number; }

export const JarodRepository = (client: $Fetch) => ({
    getDialerConnection: (options: { skipGlobalErrorAlert?: boolean } = {}) =>
        client<JarodDialerConnection>('/v1/jarod/dialer/connection', { method: 'GET', ...options }),
    createDialerConnection: (body: { provider: 'minari'; api_key: string }, options: { skipGlobalErrorAlert?: boolean } = {}) =>
        client<JarodDialerConnection>('/v1/jarod/dialer/connection', { method: 'POST', body, ...options }),
    revokeDialerConnection: () => client('/v1/jarod/dialer/connection', { method: 'DELETE' }),
    listDialerUsers: () => client<JarodDialerUser[]>('/v1/jarod/dialer/connection/users', { method: 'GET' }),
    chooseDialerUser: (body: { external_user_id: string }) => client('/v1/jarod/dialer/connection/user', { method: 'PUT', body }),
    listDialerLists: () => client<JarodDialerLists>('/v1/jarod/dialer/connection/lists', { method: 'GET' }),
    setDialerListMapping: (externalListId: string, body: { mission_id: string | null }, options: { skipGlobalErrorAlert?: boolean } = {}) =>
        client(`/v1/jarod/dialer/connection/lists/${encodeURIComponent(externalListId)}`, { method: 'PUT', body, ...options }),
    syncDialerNow: () => client('/v1/jarod/dialer/connection/sync', { method: 'POST' }),
    listDialerCalls: (query: { cursor?: string; limit: number }) =>
        client<JarodCallsPage>('/v1/jarod/dialer/calls', { method: 'GET', query }),

    listInteractions: () => client<JarodInteractionSummary[]>('/v1/jarod/interactions', { method: 'GET' }),

    getReview: (id: string) => client<JarodReview>(`/v1/jarod/interactions/${id}/review`, { method: 'GET' }),

    getVersion: (id: string, extractionId: string) =>
        client<JarodVersionItems>(`/v1/jarod/interactions/${id}/extractions/${extractionId}`, { method: 'GET' }),

    // Le composant affiche son propre message traduit (jamais la clé brute du backend).
    rerun: (id: string, options: { skipGlobalErrorAlert?: boolean } = {}) =>
        client(`/v1/jarod/interactions/${id}/rerun`, { method: 'POST', ...options }),

    eraseInteraction: (id: string) => client(`/v1/jarod/interactions/${id}`, { method: 'DELETE' }),

    eraseExpertData: (userId: string) =>
        client<{ erased_interactions: number }>(`/v1/jarod/experts/${userId}/data`, { method: 'DELETE' }),

    updateOutcome: (id: string, body: { outcome?: string | null; outcome_note?: string | null }) =>
        client(`/v1/jarod/interactions/${id}/outcome`, { method: 'PATCH', body }),

    createSignalement: (extractionId: string, body: { item_id: string; reason: string }) =>
        client<{ signalement_id: string }>(`/v1/jarod/extractions/${extractionId}/signalements`, { method: 'POST', body }),

    deleteSignalement: (signalementId: string) =>
        client(`/v1/jarod/signalements/${signalementId}`, { method: 'DELETE' }),
});

/** Clé d'erreur publique d'un refus backend (`title` du problème JSON), ou null. */
export function backendErrorKey(err: unknown): string | null {
    if (typeof err === 'object' && err !== null && 'data' in err) {
        const body = err.data;
        if (typeof body === 'object' && body !== null && 'data' in body) {
            const problem = body.data;
            if (typeof problem === 'object' && problem !== null && 'title' in problem && typeof problem.title === 'string') {
                return problem.title;
            }
        }
    }
    return null;
}
